/**
 * WHAT:
 *   Comprehensive raw data debug display component with field analysis, validation, and Supabase mapping.
 *
 * WHY:
 *   Centralizes raw data debug UI for better maintainability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Components
 *
 * DEPENDENCIES:
 *   - isEmpty utility function
 *   - formatNumber2 utility function
 *   - TypeScript types (RawDataDebugDisplayProperties)
 *
 * RISKS:
 *   - NOTE: Component renders very large JSON structures - may cause performance issues
 *   - NOTE: Clipboard operations may fail in some browsers
 *   - WARN: Complex data transformation logic - ensure correctness
 *   - NOTE: Large text areas may impact rendering performance
 */

import type { RawDataDebug, RawDataDebugDisplayProperties, ValidationStatus } from "../types";
import type { FC } from "react";

/**
 * WHAT:
 *   Component displaying comprehensive raw data debug information.
 *
 * WHY:
 *   Encapsulates raw data debug display UI logic.
 *
 * @param root0
 *
 * @param root0.rawData
 *
 * @param root0.rawPdfText
 *
 * @param root0.uploadedFile
 *
 * @param root0.validationStatus
 *
 * @param root0.securitiesDataMap
 *
 * @param root0.isLoadingSecurities
 *
 * @param root0.showRawData
 *
 * @param root0.onClose
 *
 * @param root0.formatNumber2
 *
 * @param root0.isEmpty
 *
 * @returns {JSX.Element | null} Raw data debug display component or null if not shown
 *
 */
// Reason: Component is exported via barrel export in index.ts, not directly imported elsewhere

export const RawDataDebugDisplay: FC<RawDataDebugDisplayProperties> = ({
  formatNumber2: formatNumber2Property,
  isEmpty: isEmptyProperty,
  isLoadingSecurities,
  onClose,
  rawData,
  rawPdfText,
  securitiesDataMap,
  showRawData,
  uploadedFile,
  validationStatus,
}) => {
  if (!showRawData || !rawData) {
    return null;
  }

  // Type guard: rawData is guaranteed to be RawDataDebug here
  const typedRawData: RawDataDebug = rawData;

  // Helper to render error mode display
  const renderErrorMode = () => {
    const errorMessage =
      typeof typedRawData.error === "string" ? typedRawData.error : "Unknown error";
    const pdfTextPreview =
      typeof typedRawData.pdf_text_preview === "string" ? typedRawData.pdf_text_preview : null;
    const pdfTextLength =
      typeof typedRawData.pdf_text_length === "number" ? typedRawData.pdf_text_length : 0;
    const timestamp = typeof typedRawData.timestamp === "string" ? typedRawData.timestamp : "N/A";

    return (
      <div className="mt-6 p-4 bg-red-600/20 rounded-lg border border-red-500/30">
        <h3 className="text-lg font-semibold text-red-400">🔍 Raw Data Debug (Error Mode)</h3>
        <div className="mt-4 space-y-3">
          <div className="p-3 bg-red-500/10 rounded border border-red-500/30">
            <div className="text-red-300 font-medium">❌ Parsing Failed</div>
            <div className="text-red-200 text-sm mt-1">{errorMessage}</div>
          </div>

          {pdfTextPreview !== null && (
            <div className="p-3 bg-slate-600/30 rounded">
              <div className="text-slate-300 font-medium mb-2">
                📄 PDF Text Preview ({pdfTextLength} chars)
              </div>
              <div className="bg-slate-800/50 rounded p-3 max-h-48 overflow-y-auto">
                <pre className="text-slate-300 text-xs whitespace-pre-wrap font-mono">
                  {pdfTextPreview}
                </pre>
              </div>
            </div>
          )}

          <div className="p-3 bg-slate-600/30 rounded">
            <div className="text-slate-300 font-medium mb-2">🔧 Debug Info</div>
            <div className="text-slate-400 text-sm space-y-1">
              <div>Timestamp: {timestamp}</div>
              <div>PDF Text Length: {pdfTextLength > 0 ? String(pdfTextLength) : "N/A"}</div>
              <div>
                Debug Extraction: {typedRawData.debug_extraction_failed ? "Failed" : "Success"}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              className="px-3 py-1 bg-slate-600/50 text-slate-300 rounded text-sm hover:bg-slate-600/70 transition"
              onClick={onClose}
            >
              Close Debug
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Check if this is an error debug object
  if (typedRawData.parsing_failed || typedRawData.debug_extraction_failed) {
    return renderErrorMode();
  }

  // Normal debug mode - rawData is already typed as RawDataDebug
  const isin = typeof typedRawData.isin === "string" ? typedRawData.isin : undefined;
  const txType =
    typeof typedRawData.transaction_type === "string" ? typedRawData.transaction_type : undefined;
  const fullSecuritiesData = isin ? (securitiesDataMap[isin] ?? null) : null;

  // Helper to calculate summary for schema-based data
  const calculateSchemaSummary = (
    schema: null | RawDataDebug["_schema"],
    typedValidationStatus: ValidationStatus,
  ) => {
    if (!schema) {
      return null;
    }

    const total = Array.isArray(schema.required) ? schema.required.length : 0;
    const mappedCount = Array.isArray(schema.mapped) ? schema.mapped.length : 0;
    const unmappedCount = Array.isArray(schema.unmapped)
      ? schema.unmapped.length
      : Math.max(total - mappedCount, 0);
    let completion: number;
    if (typeof schema.completion_percentage === "number") {
      completion = schema.completion_percentage;
    } else if (total > 0) {
      completion = Math.round((mappedCount / total) * 100);
    } else {
      completion = 0;
    }

    return {
      completionPercentage: completion,
      emptyFields: unmappedCount,
      fieldsWithData: mappedCount,
      importantFieldsTotal: total,
      importantFieldsWithData: mappedCount,
      importReady: typedValidationStatus.canImport,
      mappedFields: mappedCount,
      missingImportantFields: Array.isArray(schema.unmapped) ? schema.unmapped : [],
      notMappedFields: unmappedCount,
      totalFields: total,
      validationErrorCount: typedValidationStatus.errors.length,
      validationOk: typedValidationStatus.isValid,
    };
  };

  // Helper to calculate summary for non-schema data
  const calculateNonSchemaSummary = (typedValidationStatus: ValidationStatus) => {
    const mapped =
      typeof typedRawData.isin === "string" && typedRawData.isin.trim().length > 0 ? 1 : 0;
    return {
      completionPercentage: mapped > 0 ? 100 : 0,
      emptyFields: mapped > 0 ? 0 : 1,
      fieldsWithData: mapped,
      importantFieldsTotal: 1,
      importantFieldsWithData: mapped,
      importReady: typedValidationStatus.canImport,
      mappedFields: mapped,
      missingImportantFields: mapped > 0 ? [] : ["isin"],
      notMappedFields: mapped > 0 ? 0 : 1,
      totalFields: 1,
      validationErrorCount: typedValidationStatus.errors.length,
      validationOk: typedValidationStatus.isValid,
    };
  };

  // Helper to create debug data for clipboard
  const createDebugData = () => {
    const schema = typedRawData._schema ?? null;
    const mapped =
      schema && Array.isArray(schema.mapped) ? new Set(schema.mapped) : new Set<string>();

    const typedValidationStatus: ValidationStatus = validationStatus;

    return {
      extractedData: rawData,
      fieldAnalysis:
        schema && Array.isArray(schema.required)
          ? schema.required.map((field) => {
              const value = typedRawData[field];
              const isEmptyValue = isEmptyProperty(value);
              return {
                field,
                isEmpty: isEmptyValue,
                isImportant: true,
                status: mapped.has(field) ? "mapped" : "not_mapped",
                value,
              };
            })
          : [
              {
                field: "isin",
                isEmpty: isEmptyProperty(typedRawData.isin),
                isImportant: true,
                status: isEmptyProperty(typedRawData.isin) ? "not_mapped" : "mapped",
                value: typedRawData.isin,
              },
            ],
      fileMetadata: {
        fileName: uploadedFile?.name ?? "unknown",
        fileSize: uploadedFile?.size ?? 0,
        fileSizeFormatted: uploadedFile
          ? `${(uploadedFile.size / 1024 / 1024).toFixed(2)} MB`
          : "N/A",
        processedAt: new Date().toISOString(),
      },
      rawPdfText: rawPdfText,
      summary:
        calculateSchemaSummary(schema, typedValidationStatus) ??
        calculateNonSchemaSummary(typedValidationStatus),
      validation: {
        canImport: typedValidationStatus.canImport,
        errorCount: typedValidationStatus.errors.length,
        errors: typedValidationStatus.errors,
        isValid: typedValidationStatus.isValid,
        validatedAt: new Date().toISOString(),
      },
    };
  };

  // Helper to create Supabase-compatible data structure
  const createSupabaseData = () => {
    interface TransactionData {
      [key: string]: unknown;
      asset_type?: string;
      broker?: string;
      broker_fee?: number;
      composite_figi?: string;
      country?: string;
      description?: string;
      dividend_per_share?: number;
      exchange_code?: string;
      fees?: number;
      figi?: string;
      file_checksum?: string;
      file_path?: string;
      fileName?: string;
      filePath?: string;
      final_yahoo_symbol?: string;
      full_time_employees?: number;
      gross_amount?: number;
      industry?: string;
      pdf_filename?: string;
      pdf_hash?: string;
      price_per_share?: number;
      region?: string;
      sector?: string;
      security_name?: string;
      share_class_figi?: string;
      shares?: number;
      tax_amount?: number;
      taxes?: number;
      ticker?: string;
      trace_id?: string;
      transaction_date?: string;
      transaction_hash?: string;
      transaction_type?: string;
      website?: string;
      yahoo_search_symbol?: string;
      yahoo_symbol?: string;
    }

    const transactionData = typedRawData as TransactionData;
    const hasSecuritiesData = isin && (transactionData.ticker ?? transactionData.security_name);

    const securitiesForSupabase = hasSecuritiesData
      ? {
          asset_type: transactionData.asset_type ?? null,
          composite_figi: transactionData.composite_figi ?? null,
          confidence: 0.8,
          country: transactionData.country ?? null,
          description: transactionData.description ?? null,
          exchange_code: transactionData.exchange_code ?? null,
          figi: transactionData.figi ?? null,
          final_yahoo_symbol: transactionData.final_yahoo_symbol ?? transactionData.ticker ?? null,
          full_time_employees: transactionData.full_time_employees ?? null,
          industry: transactionData.industry ?? null,
          isin: isin,
          name: transactionData.security_name ?? null,
          reference_data_complete: !!(transactionData.ticker && transactionData.security_name),
          region: transactionData.region ?? null,
          sector: transactionData.sector ?? null,
          share_class_figi: transactionData.share_class_figi ?? null,
          ticker: transactionData.ticker ?? null,
          validation_type: "enrichment_lightweight",
          website: transactionData.website ?? null,
          yahoo_search_symbol: transactionData.yahoo_search_symbol ?? null,
          yahoo_symbol: transactionData.yahoo_symbol ?? transactionData.ticker ?? null,
        }
      : null;

    interface SupabaseTransaction {
      broker: null | string;
      broker_fee: null | number;
      capital_gains_tax?: null | number;
      church_tax?: null | number;
      currency: string;
      dividend_per_share?: null | number;
      exchange_rate: number;
      file_checksum: null | string;
      file_path: null | string;
      gross_amount: null | number;
      gross_dividend?: null | number;
      import_source: string;
      isin: null | string;
      net_amount: null | number;
      net_dividend?: null | number;
      parser_version: string;
      pdf_filename: null | string;
      pdf_hash: null | string;
      price: null | number;
      quantity: null | number;
      shares?: null | number;
      solidarity_surcharge?: null | number;
      tax_amount: null | number;
      trace_id: null | string;
      transaction_date: null | string;
      transaction_hash: null | string;
      transaction_type: null | string;
      withholding_tax?: null | number;
    }

    const transactionForSupabase: SupabaseTransaction = {
      broker: transactionData.broker ?? null,
      broker_fee: transactionData.fees ?? transactionData.broker_fee ?? null,
      currency: "EUR",
      exchange_rate: 1,
      file_checksum: transactionData.file_checksum ?? null,
      file_path: transactionData.filePath ?? transactionData.file_path ?? null,
      gross_amount: transactionData.gross_amount ?? null,
      import_source: "pdf_import",
      isin: isin ?? null,
      net_amount:
        typeof transactionData.net_amount === "number" ? transactionData.net_amount : null,
      parser_version: "1.0.0",
      pdf_filename: transactionData.fileName ?? transactionData.pdf_filename ?? null,
      pdf_hash: transactionData.pdf_hash ?? null,
      price: transactionData.price_per_share ?? null,
      quantity: transactionData.shares ?? null,
      tax_amount: transactionData.taxes ?? transactionData.tax_amount ?? null,
      trace_id: transactionData.trace_id ?? null,
      transaction_date: transactionData.transaction_date ?? null,
      transaction_hash: transactionData.transaction_hash ?? null,
      transaction_type: txType ?? null,
    };

    if (txType === "DIVIDEND") {
      transactionForSupabase.capital_gains_tax = null;
      transactionForSupabase.church_tax = null;
      transactionForSupabase.dividend_per_share =
        typeof transactionData.dividend_per_share === "number"
          ? transactionData.dividend_per_share
          : null;
      transactionForSupabase.gross_dividend =
        typeof transactionData.gross_amount === "number" ? transactionData.gross_amount : null;
      transactionForSupabase.net_dividend =
        typeof transactionData.net_amount === "number" ? transactionData.net_amount : null;
      transactionForSupabase.shares =
        typeof transactionData.shares === "number" ? transactionData.shares : null;
      transactionForSupabase.solidarity_surcharge = null;
      transactionForSupabase.withholding_tax =
        typeof transactionData.taxes === "number" ? transactionData.taxes : null;
    }

    const typedFullSecuritiesData = fullSecuritiesData;

    return {
      dividend_schedule: typedFullSecuritiesData?.dividend_schedule ?? null,
      price_cache: typedFullSecuritiesData?.price_cache ?? null,
      securities: securitiesForSupabase,
      securities_metrics: typedFullSecuritiesData?.securities_metrics ?? null,
      transaction: transactionForSupabase,
    };
  };

  // Helper to calculate total fields count
  const calculateTotalFields = (schema: null | RawDataDebug["_schema"]): number => {
    const hasIsin = typeof typedRawData.isin === "string" && typedRawData.isin.trim().length > 0;
    if (Array.isArray(schema?.required)) {
      return schema.required.length;
    }
    return hasIsin ? 1 : 0;
  };

  // Helper to calculate mapped count
  const calculateMappedCount = (schema: null | RawDataDebug["_schema"]): number => {
    const hasIsin = typeof typedRawData.isin === "string" && typedRawData.isin.trim().length > 0;
    if (Array.isArray(schema?.mapped)) {
      return schema.mapped.length;
    }
    return hasIsin ? 1 : 0;
  };

  // Helper to calculate unmapped count
  const calculateUnmappedCount = (schema: null | RawDataDebug["_schema"]): number => {
    const hasIsin = typeof typedRawData.isin === "string" && typedRawData.isin.trim().length > 0;
    if (Array.isArray(schema?.unmapped)) {
      return schema.unmapped.length;
    }
    return hasIsin ? 0 : 1;
  };

  // Helper to calculate completion percentage
  const calculateCompletion = (
    schema: null | RawDataDebug["_schema"],
    total: number,
    mappedCount: number,
  ): number => {
    if (typeof schema?.completion_percentage === "number") {
      return schema.completion_percentage;
    }
    if (total > 0) {
      return Math.round((mappedCount / total) * 100);
    }
    return 0;
  };

  // Helper to calculate schema statistics
  const calculateSchemaStats = () => {
    const schema = typedRawData._schema ?? null;
    const required: string[] = Array.isArray(schema?.required) ? schema.required : [];
    const requiredSet = new Set(required);
    const allEntries = Object.entries(typedRawData).filter(([key]) => key !== "_schema");
    const extraEntries = allEntries.filter(
      ([key, value]) => !requiredSet.has(key) && !isEmptyProperty(value),
    );
    const orderedEntries: [string, unknown][] = [
      ...required.map((key) => [key, typedRawData[key]] as [string, unknown]),
      ...extraEntries,
    ];

    const mapped: string[] = Array.isArray(schema?.mapped) ? schema.mapped : [];
    const unmapped: string[] = Array.isArray(schema?.unmapped) ? schema.unmapped : [];

    const total = calculateTotalFields(schema);
    const mappedCount = calculateMappedCount(schema);
    const unmappedCount = calculateUnmappedCount(schema);
    const completion = calculateCompletion(schema, total, mappedCount);

    return {
      completion,
      mapped,
      mappedCount,
      orderedEntries,
      requiredSet,
      total,
      unmapped,
      unmappedCount,
    };
  };

  const schemaStats = calculateSchemaStats();
  const {
    completion,
    mapped,
    mappedCount,
    orderedEntries,
    requiredSet,
    total,
    unmapped,
    unmappedCount,
  } = schemaStats;

  const typedValidationStatus: ValidationStatus = validationStatus;

  // Helper to render raw PDF text section
  const renderRawPdfText = () => (
    <div className="bg-slate-800/50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-md font-semibold text-white">📄 Raw PDF Text</h4>
        <div className="flex gap-2">
          <button
            className="px-2 py-1 bg-slate-600/30 text-slate-300 rounded text-xs hover:bg-slate-600/50 transition"
            onClick={() => {
              void navigator.clipboard.writeText(rawPdfText);
            }}
          >
            📋 Copy
          </button>
        </div>
      </div>
      <div
        className="bg-slate-900/50 rounded p-3"
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono">
          {rawPdfText || "No PDF text extracted yet..."}
        </pre>
      </div>
      <div className="text-xs text-slate-400 mt-2">
        Characters: {rawPdfText.length} | Lines: {rawPdfText.split("\n").length}
      </div>
    </div>
  );

  // Helper to render field analysis section
  const renderFieldAnalysis = () => (
    <div className="bg-slate-800/50 rounded-lg p-4">
      <h4 className="text-md font-semibold text-white mb-3">📊 Field Analysis</h4>
      {orderedEntries.length === 0 ? (
        <div className="text-slate-300 text-sm">Keine Felder vorhanden.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {orderedEntries.map(([key, value]) => {
            const isEmptyValue = isEmptyProperty(value);
            const isImportant = requiredSet.has(key);

            let statusClass: string;
            if (isEmptyValue) {
              statusClass = "bg-red-900/20 border-red-500/30";
            } else if (isImportant) {
              statusClass = "bg-green-900/20 border-green-500/30";
            } else {
              statusClass = "bg-slate-700/30 border-slate-500/30";
            }

            let displayValue: string;
            if (isEmptyValue) {
              displayValue = "No data";
            } else if (key === "dividend_per_share") {
              displayValue = formatNumber2Property(value);
            } else {
              displayValue = String(value);
            }

            return (
              <div className={`p-3 rounded-lg border ${statusClass}`} key={key}>
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-sm font-medium ${
                      isEmptyValue ? "text-red-400" : "text-white"
                    }`}
                  >
                    {key}
                  </span>
                  {isEmptyValue ? (
                    <span className="text-red-400 text-xs">❌ Empty</span>
                  ) : (
                    <span className="text-green-400 text-xs">✅ Found</span>
                  )}
                </div>
                <div className={`text-sm ${isEmptyValue ? "text-red-300" : "text-slate-300"}`}>
                  {displayValue}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Helper to render complete raw JSON section
  const renderCompleteRawJson = () => (
    <div className="bg-slate-800/50 rounded-lg p-4">
      <h4 className="text-md font-semibold text-white mb-3">📋 Complete Raw JSON</h4>
      <div className="bg-slate-900/50 rounded-lg p-3 max-h-64 overflow-y-auto">
        <pre className="text-slate-300 text-xs whitespace-pre-wrap font-mono">
          {JSON.stringify(rawData, null, 2)}
        </pre>
      </div>
    </div>
  );

  // Helper to render complete result JSON section
  const renderCompleteResultJson = () => (
    <div className="bg-slate-800/50 rounded-lg p-4">
      <h4 className="text-md font-semibold text-white mb-3">📦 Complete Result JSON</h4>
      <div className="mb-2 text-xs text-slate-400">
        Struktur wie sie an Supabase gesendet würde (vollständiger Workflow wie beim Import):
        <ul className="list-disc list-inside mt-1 ml-2">
          <li>
            <strong>securities</strong>: Daten für die securities-Tabelle (aus Enrichment)
          </li>
          <li>
            <strong>transaction</strong>: Daten für transactions_buy/sell/dividend-Tabelle
            (Feldnamen gemappt: shares→quantity, fees→broker_fee, taxes→tax_amount)
          </li>
          <li>
            <strong>securities_metrics</strong>: Finanzkennzahlen (market_cap, pe_ratio, beta, etc.)
            - wird geladen wenn Enrichment aktiviert ist
          </li>
          <li>
            <strong>price_cache</strong>, <strong>dividend_schedule</strong>: Werden beim Import
            optional geladen (hier: null)
          </li>
        </ul>
        {isLoadingSecurities ? (
          <div className="mt-2 text-yellow-400">
            ⏳ Lade vollständige Securities-Daten (metrics)...
          </div>
        ) : null}
      </div>
      <pre className="bg-slate-900 p-3 rounded-lg text-xs text-slate-300 overflow-auto max-h-96 border border-slate-700">
        {JSON.stringify(createSupabaseData(), null, 2)}
      </pre>
    </div>
  );

  // Helper to render mapping overview section
  const renderMappingOverview = () => (
    <div className="bg-slate-800/50 rounded-lg p-4">
      <h4 className="text-md font-semibold text-white mb-3">🗺️ Mapping Overview</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-green-900/20 rounded-lg p-3">
          <h5 className="text-sm font-semibold text-green-400 mb-2">✅ Successfully Mapped</h5>
          <div className="space-y-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
            {mapped.length > 0 ? (
              mapped.map((field) => {
                const fieldValue = typedRawData[field];
                const displayValue =
                  typeof fieldValue === "string" ? fieldValue : String(fieldValue);
                return (
                  <div className="text-xs" key={field}>
                    <span className="text-green-300 font-medium">{field}:</span>
                    <span className="text-green-100 ml-2">{displayValue}</span>
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-red-300">No fields mapped</div>
            )}
          </div>
        </div>
        <div className="bg-red-900/20 rounded-lg p-3">
          <h5 className="text-sm font-semibold text-red-400 mb-2">❌ Not Mapped</h5>
          <div className="space-y-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
            {unmapped.length > 0 ? (
              unmapped.map((field) => (
                <div className="text-xs" key={field}>
                  <span className="text-red-300 font-medium">{field}:</span>
                  <span className="text-red-100 ml-2">Empty</span>
                </div>
              ))
            ) : (
              <div className="text-xs text-green-300">All required fields mapped</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Helper to render summary section
  const renderSummary = () => (
    <div className="bg-slate-800/50 rounded-lg p-4">
      <h4 className="text-md font-semibold text-white mb-3">📈 Summary</h4>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-400">{mappedCount}</div>
          <div className="text-slate-400">Mapped</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-400">{unmappedCount}</div>
          <div className="text-slate-400">Not Mapped</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-400">{total}</div>
          <div className="text-slate-400">Total Fields</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-400">{completion}%</div>
          <div className="text-slate-400">Completion</div>
        </div>
        <div className="text-center">
          <div
            className={`text-2xl font-bold ${typedValidationStatus.isValid ? "text-green-400" : "text-red-400"}`}
          >
            {typedValidationStatus.isValid ? "✅" : "❌"}
          </div>
          <div className="text-slate-400">Validation OK</div>
        </div>
        <div className="text-center">
          <div
            className={`text-2xl font-bold ${typedValidationStatus.canImport ? "text-green-400" : "text-red-400"}`}
          >
            {typedValidationStatus.canImport ? "✅" : "❌"}
          </div>
          <div className="text-slate-400">Import Ready</div>
        </div>
      </div>

      {/* Validation Errors */}
      {!typedValidationStatus.isValid && typedValidationStatus.errors.length > 0 && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <h5 className="text-red-400 font-medium mb-2">❌ Validation Errors:</h5>
          <ul className="text-sm text-red-300 space-y-1">
            {typedValidationStatus.errors.map((error: string, index: number) => {
              const errorKey =
                error.length > 0
                  ? `${error.slice(0, 20)}-${String(error.length)}-${String(index)}`
                  : `error-${String(index)}`;
              return (
                <li className="flex items-start" key={errorKey}>
                  <span className="text-red-400 mr-2">•</span>
                  <span>{error}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-6 p-4 bg-orange-600/20 rounded-lg border border-orange-500/30">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-orange-400">🔍 Raw Data Debug</h3>
        <div className="flex gap-2">
          <button
            className="px-3 py-1 bg-blue-600/30 text-blue-300 rounded text-sm hover:bg-blue-600/50 transition"
            onClick={() => {
              void navigator.clipboard.writeText(JSON.stringify(createDebugData(), null, 2));
            }}
          >
            📋 Copy All Debug Data
          </button>
          <button
            className="px-3 py-1 bg-orange-600/30 text-orange-300 rounded text-sm hover:bg-orange-600/50 transition"
            onClick={onClose}
          >
            ✕ Close
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {renderRawPdfText()}
        {renderFieldAnalysis()}
        {renderCompleteRawJson()}
        {renderCompleteResultJson()}
        {renderMappingOverview()}
        {renderSummary()}
      </div>
    </div>
  );
};
